import { Loader2 } from "lucide-react";
import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, useToast } from "randmarcomps";
import { inject, observer } from "mobx-react";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDeleteV4ManufacturerByRouteManufacturerIdOpportunityAndBidNumberMutation } from "randmar-api-client";
import withManufacturer from "../../contexts/WithManufacturer";
import Path from "../../routes/Path";
import { RootStore } from "../../stores/rootStore";

interface IProps {
  store?: RootStore;
  applicationId: string;
  disabled?: boolean;
  buttonTitle?: string;
  text?: string;
  children: React.ReactNode;
}

const DeleteOpportunityWrapper: React.FC<IProps> = ({ store, applicationId, buttonTitle, text, children }) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const [deleteOpportunityMutation, { isLoading: loading }] = useDeleteV4ManufacturerByRouteManufacturerIdOpportunityAndBidNumberMutation();

  const deleteOpportunity = async () => {
    const { opportunityStore } = store!;
    const { opportunity } = opportunityStore;

    if (!opportunity?.bidNumber) {
      toast({ variant: "destructive", title: "Error", description: "Could not find opportunity to delete." });
      return;
    }

    try {
      await deleteOpportunityMutation({
        routeManufacturerId: applicationId,
        bidNumber: opportunity.bidNumber,
      }).unwrap();

      toast({ variant: "success", title: "Success", description: "Opportunity deleted successfully." });
      navigate(Path.opportunities(applicationId));
    } catch (err) {
      toast({ variant: "destructive", title: "Error", description: "There was an error deleting the opportunity." });
      console.error(err);
    }
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  return (
    <>
      <div onClick={handleOpen} className="inline-block cursor-pointer">
        {children}
      </div>
      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && !loading && handleCancel()}>
        <DialogContent className="sm:max-w-md">
          {loading ? (
            <div className="flex flex-col items-center justify-center p-8 gap-4">
              <Loader2 className="h-10 w-10 animate-spin" />
              <p>Please wait...</p>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>{buttonTitle}</DialogTitle>
              </DialogHeader>
              <div className="py-4">{text || "Are you sure?"}</div>
              <DialogFooter>
                <Button variant="outline" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button onClick={deleteOpportunity}>
                  Confirm
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default withManufacturer(inject('store')(observer(DeleteOpportunityWrapper)));
